import type { ICryptoAdapter, IMetadataKeyRepository, IDekContainer, IDatabasePersistenceAdapter } from '@app/domain/ports';
import { DekError } from '@app/domain/entities';
import { EncryptedDatabaseSnapshotReader } from './encrypted-database-snapshot.reader';
import { EncryptedDatabaseSnapshotWriter } from './encrypted-database-snapshot.writer';

/**
 * Infrastructure adapter for persisting the encrypted SQLite recovery snapshot
 * to/from IndexedDB.
 *
 * In worker/straight modes this is the primary persistence mechanism.
 * In OPFS mode, the snapshot is kept as a recovery copy in case the OPFS
 * database file becomes unavailable.
 *
 * Pure TypeScript class – no `@Injectable`, no `inject()`.
 * All dependencies are supplied via constructor parameters so that the class
 * can be wired up through a factory provider in `infrastructure.providers.ts`.
 */
export class EncryptedDatabasePersistenceAdapter implements IDatabasePersistenceAdapter {
  constructor(
    private readonly crypto: ICryptoAdapter,
    private readonly getDekContainer: () => IDekContainer,
    private readonly metadataRepository: IMetadataKeyRepository,
    private readonly isUnlocked: () => boolean,
  ) {}

  async load(): Promise<Uint8Array | null> {
    if (!this.isUnlocked()) {
      throw new DekError('decrypt-database', 'Cannot decrypt database: DEK not unlocked');
    }

    let decryptedDb: Uint8Array | null = null;
    const dekContainer = this.getDekContainer();

    await dekContainer.execute(async (dek) => {
      if (!dek) throw new DekError('decrypt-database', 'DEK is null');

      const reader = new EncryptedDatabaseSnapshotReader(this.crypto, this.metadataRepository);
      decryptedDb = await reader.load(dek);
    });

    return decryptedDb;
  }

  async save(dbData: Uint8Array): Promise<void> {
    if (!this.isUnlocked()) {
      throw new DekError('encrypt-database', 'Cannot encrypt database: DEK not unlocked');
    }

    const dekContainer = this.getDekContainer();

    await dekContainer.execute(async (dek) => {
      if (!dek) throw new DekError('encrypt-database', 'DEK is null');

      const writer = new EncryptedDatabaseSnapshotWriter(this.crypto, this.metadataRepository);
      await writer.persist(dbData, dek);
    });
  }
}
